// A small, accessible confirmation modal for destructive (hard-delete) and archive
// (soft-delete) actions. The shared delete dialog across the office app. The `variant`
// switches the framing: "destructive" = red (--ele-danger), permanent; "archive" = amber,
// reversible. Clicking the backdrop or Cancel dismisses; the confirm button is disabled
// while the action is in flight.
//
// Two-step confirmation (Session ACH, Director-locked platform standard): a permanent,
// irreversible delete must be confirmed TWICE. So for the "destructive" variant the confirm
// button ARMS on the first click (an escalation line appears) and only fires onConfirm on the
// second click. The "archive" variant is reversible, so it stays single-click. A genuinely
// low-stakes destructive delete (a log entry, a re-creatable saved search, a re-generatable
// tag/visit, a van-stock line) opts out with `singleStep` so the pattern is never forced onto
// trivial actions. Both cancel paths (Cancel, Esc, backdrop) disarm as well as dismiss.
//
// Session AFL: `twoStep` is the opposite opt-in, for an ARCHIVE that is not reversible in
// practice. The training soft delete hides a record everywhere and has no restore surface yet,
// so it earns the second press even though nothing is destroyed. The escalation copy follows the
// variant (an archive is never described as permanent), and every existing call site keeps its
// current behaviour because the prop defaults to the rule it replaced.
//
// Session ACW loop 4: the wrapper moved onto the shared ModalShell (portal + p-4 overlay +
// Escape via the shell); the two-step arming behaviour above is a locked platform rule and is
// unchanged.
import { useEffect, useState, type ReactNode } from "react";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "./ModalShell";

export type ConfirmVariant = "destructive" | "archive";

export function ConfirmDeleteModal({
  open,
  title,
  message,
  warning,
  confirmLabel = "Delete",
  variant = "destructive",
  singleStep = false,
  twoStep: twoStepProp,
  busy = false,
  error,
  children,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  // An optional prominent amber warning (e.g. "This contract has an active billing arrangement"),
  // shown above the buttons when present.
  warning?: string | null;
  confirmLabel?: string;
  variant?: ConfirmVariant;
  // Opt a destructive delete OUT of the two-step confirmation (low-stakes / reversible-in-effect
  // actions only). Ignored for the archive variant, which is single-step by nature.
  singleStep?: boolean;
  // Force the two-step confirmation on or off explicitly. Left unset (every existing call site)
  // it resolves to the locked default: destructive arms, archive does not. Set it on an archive
  // that the office cannot actually undo from the app.
  twoStep?: boolean;
  busy?: boolean;
  error?: string | null;
  // Optional extra controls shown under the message (a choice that changes what the confirm does).
  children?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const archive = variant === "archive";
  const twoStep = twoStepProp ?? (!archive && !singleStep);
  // The first click of a two-step delete ARMS the confirm; the second fires it.
  const [armed, setArmed] = useState(false);

  // Reset the armed state whenever the dialog opens or closes, so a re-opened dialog always
  // starts from the first (un-armed) step.
  useEffect(() => {
    if (!twoStep) return;
    setArmed(false);
  }, [open, twoStep]);

  function cancel() {
    setArmed(false);
    onCancel();
  }

  if (!open) return null;

  const panelBorder = archive ? "border-amber-500" : "border-ele-danger";
  const titleColour = archive ? "text-[var(--ele-amber-text)]" : "text-ele-danger";
  // The delete fill is the fixed #c0392b token (white ink 5.44:1 on both themes), never bg-ele-danger,
  // which is #FF5A5A on the dark theme and 3.06:1 under white ink (defect D4). The archive fill is amber
  // with dark ink. Two lines, so the one is never read beside the other.
  const confirmBtnArchive = "bg-amber-500 text-ele-bg";
  const confirmBtnDelete = "bg-ele-danger-fill text-white";
  const confirmBtn = archive ? confirmBtnArchive : confirmBtnDelete;
  const busyLabel = archive ? "Archiving..." : "Deleting...";

  function onConfirmClick() {
    if (twoStep && !armed) {
      setArmed(true);
      return;
    }
    onConfirm();
  }

  return (
    <ModalShell
      ariaLabel={title}
      onClose={() => !busy && cancel()}
      closeOnBackdrop
      width="max-w-md"
      border={panelBorder}
      panelClassName="p-5 shadow-xl"
    >
        <h2 className={`font-heading text-lg ${titleColour}`}>{title}</h2>
        <p className="mt-2 text-sm text-ele-body">{message}</p>
        {children}
        {warning && (
          <p className="mt-3 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text-soft)]">
            {warning}
          </p>
        )}
        {/* Second-step escalation: shown once the delete is armed, so the final click is a
            deliberate, unmistakable confirmation. An archive is never called permanent: it says
            only that the press is the one that does it. */}
        {twoStep && armed && (
          <p
            role="alert"
            className={`mt-3 rounded-ele px-3 py-2 text-sm ${
              archive
                ? "border border-amber-500 bg-amber-500/10 text-[var(--ele-amber-text)]"
                : "border border-ele-danger bg-ele-danger/10 text-ele-danger"
            }`}
          >
            {archive ? "" : "This is permanent and cannot be undone. "}Press "{confirmLabel}" once more to confirm.
          </p>
        )}
        {error && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {error}
          </p>
        )}
        <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirmClick}
            disabled={busy}
            data-armed={twoStep && armed ? "true" : undefined}
            className={`rounded-ele px-5 py-2 font-heading text-sm tracking-wide disabled:opacity-50 ${confirmBtn} ${MODAL_ACTION_BTN} ${
              twoStep && armed
                ? `ring-2 ring-offset-2 ring-offset-[var(--ele-shell)] ${archive ? "ring-amber-500" : "ring-ele-danger"}`
                : ""
            }`}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
    </ModalShell>
  );
}

// The exact confirmation copy for the quote/call-out hard delete (Director-specified).
export const DELETE_CONFIRM_MESSAGE =
  "This will permanently delete this record and remove the associated Xero draft. This cannot be undone.";
